import { useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCacheCgdo } from 'app/shared/queries/useCacheCgdo';
import { computeDlHashIdForCgdo } from 'app/shared/utils/dlHashId';

// Components
import {
  U21SelectSearch,
  U21SelectSearchProps,
} from 'app/shared/u21-ui/components';
import { IconCreditCard } from '@u21/tabler-icons';

// Selectors
import {
  selectAgentOrgID,
  selectHasReadInstrumentsPermission,
} from 'app/modules/session/selectors';

// Constants
import { OFFSET_DEFAULT } from 'app/shared/constants';

// Helpers
import { formatInstrumentOptions } from 'app/modules/search/helpers';
import { useQueryClient } from '@tanstack/react-query';
import { InstrumentSearchOptions, MatchType } from 'app/modules/search/enum';
import {
  searchInstruments,
  useInstrumentSearch,
} from 'app/modules/instruments/queries/useInstrumentSearch';
import { INSTRUMENT_QUERY_KEYS } from 'app/modules/instruments/queries/keys';
import { InstrumentSearchPayload } from 'app/modules/search/payload';

export type U21InstrumentSearchProps<TClearable extends boolean> =
  DistributiveOmit<
    U21SelectSearchProps<string, TClearable>,
    'options' | 'onSearch'
  >;

export const U21InstrumentSearch = <TClearable extends boolean>(
  props: U21InstrumentSearchProps<TClearable>,
) => {
  const { multi, value } = props;

  const queryClient = useQueryClient();

  const hasReadInstrumentsPermissions = useSelector(
    selectHasReadInstrumentsPermission,
  );
  const [newPayload, setNewPayload] = useState<InstrumentSearchPayload>();
  const [searchValue, setSearchValue] = useState<string>('');

  const {
    instruments,
    query: { isFetching, isError: isV2Error },
    refreshInstruments,
  } = useInstrumentSearch(newPayload, true);

  const options = useMemo(
    () => formatInstrumentOptions(instruments),
    [instruments],
  );

  const orgId = useSelector(selectAgentOrgID);
  const { mutate: cacheCgdo } = useCacheCgdo();

  const cachePrefetch = useCallback(
    (newValues: string[]) => {
      const prevSet = new Set(Array.isArray(value) ? value : []);
      newValues.forEach((i) => {
        if (prevSet.has(i)) return;
        cacheCgdo({
          object_type: 'instrument',
          hashed_id: computeDlHashIdForCgdo(orgId, String(i)),
        });
      });
    },
    [cacheCgdo, orgId, value],
  );

  // calledRef is used to prevent an infinite loop in case the API never returns with the missing value
  const calledRef = useRef(false);
  useEffect(() => {
    calledRef.current = false;
  }, [value]);

  useEffect(() => {
    if (!hasReadInstrumentsPermissions) {
      return;
    }

    let missingValues: string[] = [];

    if (Array.isArray(value)) {
      missingValues = value.filter((i) => !options.find((j) => j.value === i));
    } else if (value !== undefined) {
      const valueExists = options.find((i) => i.value === value);
      if (!valueExists) {
        missingValues = [value];
      }
    }

    if (missingValues.length && !calledRef.current) {
      calledRef.current = true;

      const payload: InstrumentSearchPayload = {
        offset: OFFSET_DEFAULT,
        limit: missingValues.length,
        search_conditions: [
          {
            phrases: missingValues.map(String),
            field: InstrumentSearchOptions.EXTERNAL_ID,
            match_type: MatchType.EXACT,
          },
        ],
      };

      queryClient.prefetchQuery({
        queryKey: INSTRUMENT_QUERY_KEYS.getInstrumentSearch(payload),
        queryFn: () => searchInstruments(payload),
      });

      setNewPayload(payload);
    }
  }, [hasReadInstrumentsPermissions, options, queryClient, value]);

  const onSearch = useCallback((query?: string) => {
    setSearchValue(query || '');

    if (query) {
      setNewPayload({
        offset: OFFSET_DEFAULT,
        limit: 10,
        search_conditions: [
          {
            phrases: [query],
            field: InstrumentSearchOptions.EXTERNAL_ID,
            match_type: MatchType.EXACT,
          },
        ],
      });
    }
  }, []);

  if (!hasReadInstrumentsPermissions) {
    return null;
  }

  let noOptionsText = 'Type to search or paste a list of Instrument IDs';
  if (isV2Error) {
    noOptionsText = 'Search failed. Please try again.';
  } else if (searchValue) {
    noOptionsText = 'No results found';
  }

  const sharedProps: DistributivePick<
    U21SelectSearchProps<string, TClearable>,
    | 'loading'
    | 'options'
    | 'onExactSearch'
    | 'onSearch'
    | 'startIcon'
    | 'noOptionsText'
  > = {
    loading: isFetching,
    options,
    onExactSearch: async (searchTerms: string[]) => {
      const payload: InstrumentSearchPayload = {
        offset: OFFSET_DEFAULT,
        limit: searchTerms.length,
        search_conditions: [
          {
            phrases: searchTerms.map(String),
            field: InstrumentSearchOptions.EXTERNAL_ID,
            match_type: MatchType.EXACT,
          },
        ],
      };

      await queryClient.prefetchQuery({
        gcTime: Infinity,
        queryKey: INSTRUMENT_QUERY_KEYS.getInstrumentSearch(payload),
        queryFn: () => searchInstruments(payload),
      });
      refreshInstruments();
    },
    onSearch,
    startIcon: <IconCreditCard />,
    noOptionsText,
  };

  if (multi) {
    const { onChange, ...rest } = props;
    return (
      <U21SelectSearch<string, TClearable>
        {...sharedProps}
        {...rest}
        multi
        onChange={(newValues, e) => {
          cachePrefetch(newValues);
          onChange?.(newValues, e);
        }}
      />
    );
  }
  const { onChange, ...rest } = props;
  return (
    <U21SelectSearch<string, TClearable>
      {...sharedProps}
      {...rest}
      onChange={(newValue, e) => {
        onChange?.(newValue, e);
      }}
    />
  );
};
